/**
 * Activity modes (the user's standing rule, 2026-10-08): every activity offers a choice, vs NPCs or vs players.
 * `npc` is played offline now (ring regulars, offline bodies, townsfolk extras, school helpers); `pvp` is a stub
 * for the server (docs/ONLINE.md): with no players found it falls back to `npc`. The mode is data per activity
 * and is chosen at sign-up. Guild war keeps its own alias (`GuildWarMode`, data/guild.ts) of the same two values.
 */
import { GUILD_WAR_MODES } from './guild';

export type ActivityMode = 'npc' | 'pvp';

export const ACTIVITY_MODES: Readonly<Record<ActivityMode, { readonly label: string }>> = GUILD_WAR_MODES;

export const isActivityMode = (v: unknown): v is ActivityMode => v === 'npc' || v === 'pvp';

/** The activities that offer the choice, and which modes each offers (data; add new activities here). */
export const MODE_ACTIVITIES = {
  arena: { label: 'ประลองยุทธ', modes: ['npc', 'pvp'] },
  ladder: { label: 'บันไดประลอง', modes: ['npc', 'pvp'] },
  duel: { label: 'ลานประลอง', modes: ['npc', 'pvp'] },
  dungeon: { label: 'ถ้ำคลื่นคำราม', modes: ['npc', 'pvp'] },
  /** บอสโลก (data/worldBosses.ts): simulated players join in npc; pvp makes the arena a contested zone. */
  worldBoss: { label: 'บอสโลก', modes: ['npc', 'pvp'] },
  /** Daily events (งานประจำวัน) that are contests or shows with performers: `event:<id>`. */
  'event:martial': { label: 'สาธิตวรยุทธ์', modes: ['npc', 'pvp'] },
  /** World events of the new Yanyu (data/worldEvents.ts): the raid, the fair, the storm. */
  'event:raid': { label: 'โจรเนินใต้บุกเมือง', modes: ['npc', 'pvp'] },
  'event:fair': { label: 'ตลาดนัดสองฝั่งน้ำ', modes: ['npc', 'pvp'] },
  /** The hidden master's descent test (data/hiddenMaster.ts): npc a spar after it, pvp a race. */
  'secret:master': { label: 'บททดสอบผู้เฒ่าเหยียบเมฆ', modes: ['npc', 'pvp'] },
  /** The red lantern (data/redLantern.ts): npc is personal, pvp is shared and falls back to an NPC rival offline. */
  redLantern: { label: 'โคมแดง', modes: ['npc', 'pvp'] },
  'event:storm': { label: 'พายุซัดปากอ่าว', modes: ['npc', 'pvp'] },
} as const satisfies Record<string, { label: string; modes: readonly ActivityMode[] }>;

export type ModeActivityId = keyof typeof MODE_ACTIVITIES;

export const isModeActivity = (v: unknown): v is ModeActivityId =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(MODE_ACTIVITIES, v);

/** The activity id of a daily event, when it offers a choice. */
export const eventActivity = (eventId: string): ModeActivityId | null => {
  const id = `event:${eventId}`;
  return isModeActivity(id) ? id : null;
};

/**
 * The mode an activity runs in: what was asked for if the activity offers it and (for `pvp`) players were found,
 * else `npc`. Pure: the caller says whether players are found (`pvpFound`; the offline World says no).
 */
export function resolveMode(
  asked: ActivityMode | undefined,
  offered: readonly ActivityMode[],
  pvpFound: boolean,
): { mode: ActivityMode; fellBack: boolean } {
  const want: ActivityMode = asked && offered.includes(asked) ? asked : 'npc';
  if (want === 'pvp' && !pvpFound) return { mode: 'npc', fellBack: true };
  return { mode: want, fellBack: false };
}
